/**
 * EDIT PHOTO CAPTIONS UTILITY
 * 
 * Export photo captions to CSV, edit in Excel/Sheets, and re-import.
 * Handles UTF-8 encoding for special characters like •
 * 
 * Route: /edit-photo-captions
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Upload, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { collection, getDocs, doc, writeBatch } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import type { CollectionItem, Photo } from '../types';

interface PhotoRow {
  itemId: string;
  brand: string;
  name: string;
  photoIndex: number;
  photoUrl: string;
  caption: string;
  isDefault: boolean;
}

export default function EditPhotoCaptions() {
  const navigate = useNavigate();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [items, setItems] = useState<CollectionItem[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [importStats, setImportStats] = useState<{ updated: number; skipped: number } | null>(null);

  // Auth check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUserId(user.uid);
        loadItems(user.uid);
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Load all items with photos
  const loadItems = async (userId: string) => {
    try {
      setLoading(true);
      const itemsRef = collection(db, 'users', userId, 'items');
      const snapshot = await getDocs(itemsRef);
      
      const loadedItems: CollectionItem[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data() as CollectionItem;
        if (data.photos && data.photos.length > 0) {
          loadedItems.push({ ...data, id: doc.id });
        }
      });
      
      setItems(loadedItems);
    } catch (error) {
      console.error('Error loading items:', error);
      setMessage({ type: 'error', text: 'Error loading items' });
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV
  const handleExport = () => {
    setExporting(true);
    setMessage(null);
    
    try {
      // Build rows
      const rows: PhotoRow[] = [];
      items.forEach(item => {
        if (item.photos) {
          item.photos.forEach((photo, index) => {
            rows.push({
              itemId: item.id,
              brand: item.brand || '',
              name: item.name || '',
              photoIndex: index,
              photoUrl: photo.url || '',
              caption: photo.caption || '',
              isDefault: photo.isDefault || false
            });
          });
        }
      });

      // Create CSV content with UTF-8 BOM for Excel compatibility
      const headers = ['itemId', 'brand', 'name', 'photoIndex', 'photoUrl', 'caption', 'isDefault'];
      const csvContent = [
        headers.join(','),
        ...rows.map(row => [
          escapeCSV(row.itemId),
          escapeCSV(row.brand),
          escapeCSV(row.name),
          row.photoIndex.toString(),
          escapeCSV(row.photoUrl),
          escapeCSV(row.caption),
          row.isDefault ? 'true' : 'false'
        ].join(','))
      ].join('\n');

      // Add UTF-8 BOM
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
      
      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photo-captions-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: `Exported ${rows.length} photos from ${items.length} items` });
    } catch (error) {
      console.error('Error exporting:', error);
      setMessage({ type: 'error', text: 'Error exporting to CSV' });
    } finally {
      setExporting(false);
    }
  };

  // Escape CSV field
  const escapeCSV = (value: string): string => {
    if (!value) return '';
    // If contains comma, quote, or newline, wrap in quotes and escape existing quotes
    if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
      return '"' + value.replace(/"/g, '""') + '"';
    }
    return value;
  };

  // Parse CSV field
  const parseCSV = (content: string): string[][] => {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentField = '';
    let inQuotes = false;
    
    for (let i = 0; i < content.length; i++) {
      const char = content[i];
      const nextChar = content[i + 1];
      
      if (inQuotes) {
        if (char === '"') {
          if (nextChar === '"') {
            // Escaped quote
            currentField += '"';
            i++;
          } else {
            // End of quoted field
            inQuotes = false;
          }
        } else {
          currentField += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          currentRow.push(currentField);
          currentField = '';
        } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
          currentRow.push(currentField);
          if (currentRow.length > 0) {
            rows.push(currentRow);
          }
          currentRow = [];
          currentField = '';
          if (char === '\r') i++;
        } else if (char !== '\r') {
          currentField += char;
        }
      }
    }
    
    // Don't forget last field/row
    if (currentField || currentRow.length > 0) {
      currentRow.push(currentField);
      rows.push(currentRow);
    }
    
    return rows;
  };

  // Import from CSV
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentUserId) return;

    setImporting(true);
    setMessage(null);
    setImportStats(null);

    try {
      // Read file as UTF-8
      const text = await file.text();
      
      // Remove BOM if present
      const content = text.replace(/^\uFEFF/, '');
      
      // Parse CSV
      const rows = parseCSV(content);
      
      if (rows.length < 2) {
        setMessage({ type: 'error', text: 'CSV file is empty or invalid' });
        return;
      }

      // Get headers
      const headers = rows[0].map(h => h.trim().toLowerCase());
      const itemIdIndex = headers.indexOf('itemid');
      const photoIndexIndex = headers.indexOf('photoindex');
      const captionIndex = headers.indexOf('caption');

      if (itemIdIndex === -1 || photoIndexIndex === -1 || captionIndex === -1) {
        setMessage({ type: 'error', text: 'CSV must have itemId, photoIndex, and caption columns' });
        return;
      }

      // Group updates by item
      const updatesByItem: Map<string, Map<number, string>> = new Map();
      
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length <= Math.max(itemIdIndex, photoIndexIndex, captionIndex)) continue;
        
        const itemId = row[itemIdIndex]?.trim();
        const photoIndex = parseInt(row[photoIndexIndex]?.trim() || '0', 10);
        const caption = row[captionIndex] || '';
        
        if (!itemId || isNaN(photoIndex)) continue;
        
        if (!updatesByItem.has(itemId)) {
          updatesByItem.set(itemId, new Map());
        }
        updatesByItem.get(itemId)!.set(photoIndex, caption);
      }

      // Apply updates in batches
      let updated = 0;
      let skipped = 0;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (const [itemId, photoUpdates] of updatesByItem) {
        const item = items.find(i => i.id === itemId);
        if (!item || !item.photos) {
          skipped += photoUpdates.size;
          continue;
        }

        // Update photos array
        const updatedPhotos = item.photos.map((photo, index) => {
          if (photoUpdates.has(index)) {
            const newCaption = photoUpdates.get(index)!;
            if (newCaption !== (photo.caption || '')) {
              updated++;
              return { ...photo, caption: newCaption || undefined };
            }
          }
          return photo;
        });

        // Clean undefined captions (Firestore doesn't like undefined)
        const cleanedPhotos = updatedPhotos.map(photo => {
          const cleaned: Photo = { url: photo.url, isDefault: photo.isDefault };
          if (photo.caption) cleaned.caption = photo.caption;
          if (photo.creditName) cleaned.creditName = photo.creditName;
          if (photo.creditUrl) cleaned.creditUrl = photo.creditUrl;
          return cleaned;
        });

        const itemRef = doc(db, 'users', currentUserId, 'items', itemId);
        batch.update(itemRef, { photos: cleanedPhotos });
        batchCount++;

        // Firestore batch limit is 500
        if (batchCount >= 400) {
          await batch.commit();
          batch = writeBatch(db); // Create new batch
          batchCount = 0;
        }
      }

      // Commit remaining
      if (batchCount > 0) {
        await batch.commit();
      }

      setImportStats({ updated, skipped });
      setMessage({ type: 'success', text: `Import complete! ${updated} captions updated.` });
      
      // Reload items to reflect changes
      loadItems(currentUserId);

    } catch (error) {
      console.error('Error importing:', error);
      setMessage({ type: 'error', text: 'Error importing CSV. Make sure the file format is correct.' });
    } finally {
      setImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-glass-gradient flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-fuchsia-400 mx-auto mb-4" />
          <p className="text-glass-secondary">Loading items...</p>
        </div>
      </div>
    );
  }

  const totalPhotos = items.reduce((sum, item) => sum + (item.photos?.length || 0), 0);

  return (
    <div className="min-h-screen bg-glass-gradient">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-glass" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-glass font-heading">
              Edit Photo Captions
            </h1>
            <p className="text-glass-secondary">
              Export captions to CSV, edit in Excel or Sheets, then re-import
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="glass-card p-6 mb-6">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold text-fuchsia-400">{items.length}</p>
              <p className="text-sm text-glass-muted">Items with Photos</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-purple-400">{totalPhotos}</p>
              <p className="text-sm text-glass-muted">Total Photos</p>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-4 rounded-xl mb-6 flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-500/20 border border-green-500/50 text-green-200'
              : 'bg-red-500/20 border border-red-500/50 text-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Import Stats */}
        {importStats && (
          <div className="p-4 rounded-xl mb-6 bg-blue-500/20 border border-blue-500/50 text-blue-200">
            <p><strong>Updated:</strong> {importStats.updated} captions</p>
            <p><strong>Skipped:</strong> {importStats.skipped} (item not found or no change)</p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-6">
          {/* Export */}
          <div className="glass-card p-6">
            <h2 className="text-xl font-bold text-glass mb-4">Step 1: Export</h2>
            <p className="text-glass-secondary mb-4">
              Download a CSV file with all your photo captions. Open it in Excel, Google Sheets, or any spreadsheet app.
            </p>
            <button
              onClick={handleExport}
              disabled={exporting || items.length === 0}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white rounded-xl font-bold hover:from-fuchsia-600 hover:to-purple-600 transition-all disabled:opacity-50"
            >
              {exporting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              Export Captions to CSV
            </button>
          </div>

          {/* Edit Instructions */}
          <div className="glass-card p-6">
            <h2 className="text-xl font-bold text-glass mb-4">Step 2: Edit</h2>
            <p className="text-glass-secondary mb-2">
              Open the CSV in your spreadsheet app and edit the <strong>caption</strong> column.
            </p>
            <ul className="text-glass-secondary text-sm space-y-1 ml-4 list-disc">
              <li>Don't change the <code className="bg-slate-700 px-1 rounded">itemId</code> or <code className="bg-slate-700 px-1 rounded">photoIndex</code> columns</li>
              <li>Special characters like • are supported</li>
              <li>Save as CSV (UTF-8) when done</li>
            </ul>
          </div>

          {/* Import */}
          <div className="glass-card p-6">
            <h2 className="text-xl font-bold text-glass mb-4">Step 3: Import</h2>
            <p className="text-glass-secondary mb-4">
              Upload your edited CSV to update the captions.
            </p>
            <label className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold hover:from-green-600 hover:to-emerald-600 transition-all cursor-pointer w-fit">
              {importing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Upload className="w-5 h-5" />
              )}
              {importing ? 'Importing...' : 'Import CSV'}
              <input
                type="file"
                accept=".csv"
                onChange={handleImport}
                disabled={importing}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
